import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { Reveal } from "../components/Motion";
import EditorialMarquee from "../components/EditorialMarquee";
import { PRODUCTS } from "../lib/data";
import Faq from "../components/Faq";

export default function Sustainable() {
    const schema = {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Sustainable Paper Packaging Manufacturing",
        serviceType: "Manufacturing of compostable & food-safe paper packaging",
        description: "Manufacturing of sustainable, food-safe paper packaging in Bangalore — PE-coated and aqueous-coated (repulpable, home-compostable) paper salad bowls, soup bowls, kraft containers and PET flat lids. No PLA linings. FSSAI, KSPCB, NABL-tested.",
        areaServed: [{ "@type": "Country", name: "India" }, { "@type": "Country", name: "United Arab Emirates" }, { "@type": "Country", name: "United Kingdom" }, { "@type": "Country", name: "Germany" }],
        provider: { "@type": "Organization", "@id": "https://thepuropack.com/#organization", name: "The Puro Pack", url: "https://thepuropack.com" },
        audience: { "@type": "BusinessAudience", audienceType: "Restaurants, QSR chains, cloud kitchens, corporate cafeterias, exporters" },
    };
    return (
        <div data-testid="sustainable-page">
            <Seo title="Sustainable Packaging — PE & Aqueous Coated, Food Safe" description="Sustainable packaging manufactured in Bangalore. Food-safe paper bowls and containers with PE or Aqueous coating options — no PLA, no compromises." path="/sustainable-packaging" schema={schema} />
            <div className="max-w-[1600px] mx-auto px-6 lg:px-10">
                <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Sustainability", to: "/sustainable-packaging" }]} />
            </div>

            <section className="max-w-[1600px] mx-auto px-6 lg:px-10 pt-14 pb-20 grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-8">
                    <div className="font-mono text-xs uppercase tracking-widest text-clay mb-6">[ Green Technology Partner ]</div>
                    <h1 className="font-serif text-6xl lg:text-9xl leading-[0.92] tracking-tight text-forest">
                        Sustainability, without the <em className="italic">asterisk.</em>
                    </h1>
                </div>
                <div className="lg:col-span-4 lg:pt-24">
                    <p className="text-inkgreen/80 leading-relaxed">
                        We manufacture paper foodservice packaging in Bangalore using two food-safe systems — traditional PE coating and water-based Aqueous coating — matched to the temperature and shelf-life your kitchen actually needs. No PLA blends. No compromises on food-contact.
                    </p>
                </div>
            </section>

            <EditorialMarquee items={["Food-Safe Coatings", "PE & Aqueous", "Made in Bangalore", "Green Technology Partner", "Est. 2025"]} />

            {/* RAW MATERIAL */}
            <section className="max-w-[1600px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-10 items-center" data-testid="raw-material-section">
                <div className="lg:col-span-6 order-2 lg:order-1">
                    <div className="font-mono text-xs uppercase tracking-widest text-clay mb-6">[ Raw Material ]</div>
                    <h2 className="font-serif text-5xl lg:text-7xl leading-[1] tracking-tight text-forest">
                        It starts with the <em className="italic">right paper.</em>
                    </h2>
                    <p className="mt-8 text-inkgreen/80 leading-relaxed max-w-xl text-lg">
                        Every bowl we ship begins as a reel of food-grade paperboard — virgin kraft for our brown range, ECF-bleached white board for our printed range. We inspect every reel on arrival for GSM tolerance, brightness, and moisture content before it ever touches a coating line.
                    </p>
                    <div className="grid grid-cols-2 gap-6 mt-10 max-w-lg">
                        <div className="border-t border-forest/30 pt-4">
                            <div className="font-mono text-xs uppercase tracking-widest text-stone">GSM range</div>
                            <div className="font-serif text-3xl text-forest mt-1">300 – 350</div>
                        </div>
                        <div className="border-t border-forest/30 pt-4">
                            <div className="font-mono text-xs uppercase tracking-widest text-stone">Source</div>
                            <div className="font-serif text-3xl text-forest mt-1">FSC-mix</div>
                        </div>
                    </div>
                </div>
                <div className="lg:col-span-6 order-1 lg:order-2">
                    <div className="image-frame aspect-[4/5]">
                        <img src="https://customer-assets-v7afamib.emergentagent.net/job_eco-pack-portal-1/artifacts/gc3osjbc_AdobeStock_1530576651.webp" alt="Rolls of food-grade white paperboard — the raw material for The Puro Pack sustainable paper packaging" loading="lazy" />
                    </div>
                </div>
            </section>

            <section className="max-w-[1600px] mx-auto px-6 lg:px-10 py-24">
                {[
                    { n: "01", h: "Two coating systems, one standard.", b: "We offer a food-safe PE coating for hot, oily and long-shelf-life applications, and a water-based Aqueous coating for cold, dry and lower-heat use. Same food-safety standard. Different circular endpoints." },
                    { n: "02", h: "No PLA. No blends.", b: "PLA sounds green but rarely composts in the real world. We don't ship it. Our board is virgin kraft or FSC-mix paperboard — traceable, tested, and honest." },
                    { n: "03", h: "Traceable fibre.", b: "We source virgin kraft from FSC-mix certified mills. Every batch ships with a certificate of origin. White-printed grades use ECF (elemental chlorine free) bleached pulp." },
                    { n: "04", h: "Circular by design.", b: "PET lids are made from recyclable food-grade rPET and pair with existing PET recycling streams. Paper bowls compost commercially with the Aqueous grade." },
                ].map((row) => (
                    <div key={row.n} className="border-t border-forest/30 py-10 grid lg:grid-cols-12 gap-8">
                        <div className="lg:col-span-2 font-mono text-5xl text-clay">[{row.n}]</div>
                        <h3 className="lg:col-span-5 font-serif text-3xl lg:text-4xl text-forest tracking-tight">{row.h}</h3>
                        <p className="lg:col-span-5 text-inkgreen/80 leading-relaxed">{row.b}</p>
                    </div>
                ))}
            </section>

            <section className="bg-forest text-bone">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-24">
                    <div className="font-mono text-xs uppercase tracking-widest opacity-70 mb-6">[ Full Catalogue ]</div>
                    <h2 className="font-serif text-5xl lg:text-7xl tracking-tight mb-16">Every product, ranked by <em className="italic">how far</em> it travels.</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {PRODUCTS.map((p) => (
                            <Link key={p.slug} to={`/products/${p.slug}`} data-testid={`sustain-product-${p.slug}`} className="block group border border-bone/30 hover:border-bone p-6 transition-colors">
                                <div className="image-frame aspect-square mb-4" style={{ borderColor: "#F4F2EC" }}>
                                    <img src={p.image} alt={p.title} loading="lazy" />
                                </div>
                                <div className="font-mono text-xs uppercase tracking-widest text-clay">Chapter {p.chapter}</div>
                                <div className="font-serif text-2xl mt-2">{p.title}</div>
                                <div className="text-xs opacity-70 mt-3 group-hover:opacity-100">Read specifications →</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <Faq />
        </div>
    );
}
